// src/pages/VehicleV2/FuelConsumptionListing.jsx
// Backend mount:
//   app.use("/api/fuelConsumptionListing", fuelConsumptionListing);
//
// APIs used:
//   GET  {BASE_URL}/api/fuelConsumptionListing/init?loginUserId=&branchId=&branchType=
//   GET  {BASE_URL}/api/fuelConsumptionListing/list?loginUserId=&branchId=&vehicleType=&branch=&storeId=&vehicleNumber=&vehicleSubtypeId=&vehicleCode=&vehicleName=&search=&page=&limit=
//   GET  {BASE_URL}/api/fuelConsumptionListing/viewall?branchId=&vehicleType=&branch=&storeId=&vehicleNumber=&vehicleSubtypeId=&vehicleCode=&vehicleName=
//   POST {BASE_URL}/api/fuelConsumptionListing/delete   body: { ids:[...], loginUserId }
//
// Focus fix:
// - filter keystrokes no longer recreate loadList
// - text inputs stay enabled while searching
// - list reloads only on Search / Enter / Clear / View All / vehicle type / pagination

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaEye,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/fuelConsumptionListing`;

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const clean = (v) => String(v ?? "").trim();

const ConfirmDialog = ({
  open,
  isDark,
  title,
  message,
  confirmText,
  cancelText,
  danger,
  primaryBtnClass,
  ghostBtnClass,
  busy,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={busy ? undefined : onCancel}
      />
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-2xl border p-6 ${
          isDark
            ? "bg-gradient-to-br from-gray-800 to-gray-900 border-white/10 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              danger
                ? isDark
                  ? "bg-red-900/40 text-red-300"
                  : "bg-red-100 text-red-600"
                : isDark
                  ? "bg-blue-900/40 text-blue-300"
                  : "bg-blue-100 text-blue-700"
            }`}
          >
            <FaExclamationTriangle />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
              {title}
            </h3>
            <p className={`mt-2 text-sm leading-6 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {message}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`p-2 rounded-lg ${
              isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-500"
            }`}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onCancel} disabled={busy} className={ghostBtnClass}>
            {cancelText || "Cancel"}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`${
              danger
                ? "flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white bg-red-600 hover:bg-red-700"
                : primaryBtnClass
            } ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FuelConsumptionListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const getSS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = sessionStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid");
  const branchTypeLS =
    getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const loginUserIdLS = getSS("loginUserId") || userIdLS;

  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branch = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const loginUserId = useMemo(
    () => toInt(loginUserIdLS, 0),
    [loginUserIdLS]
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-branchtype": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [vehicleType, setVehicleType] = useState("HireVehicle");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [filters, setFilters] = useState({
    vehicleNumber: "",
    vehicleCode: "",
    storeId: "",
    branch: "",
    vehicleSubtypeId: "",
    vehicleName: "",
  });

  const [stores, setStores] = useState([]);
  const [branches, setBranches] = useState([]);
  const [vehicleSubtypes, setVehicleSubtypes] = useState([]);

  const [list, setList] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDlg, setConfirmDlg] = useState({
    open: false,
    title: "",
    message: "",
    confirmText: "Delete",
    cancelText: "Cancel",
    danger: true,
  });

  const filtersRef = useRef(filters);
  const searchRef = useRef(search);
  const vehicleTypeRef = useRef(vehicleType);
  const pageRef = useRef(page);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    vehicleTypeRef.current = vehicleType;
  }, [vehicleType]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(toInt(total, 0) / limit)),
    [total, limit]
  );

  const isBusy = pageLoading || loading || deleting;

  const allSelected =
    list.length > 0 &&
    list.every((r) => selectedIds.includes(r.id));

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const ghostBtnClass = `flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
    isDark
      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
  }`;

  const primaryBtnClass = `flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`;

  const resetSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const validateBeforeAction = useCallback(
    ({ requireBranch = false, requireLogin = false } = {}) => {
      if (requireBranch && !branchId) {
        toast.error("BranchId not found");
        return false;
      }

      if (requireLogin && !loginUserId) {
        toast.error("LoginUserId not found");
        return false;
      }

      return true;
    },
    [branchId, loginUserId]
  );

  const buildListParams = useCallback(
    (pageNo = 1, nextFilters = filtersRef.current, nextSearch = searchRef.current) => ({
      loginUserId,
      branchId,
      vehicleType: vehicleTypeRef.current,
      search: clean(nextSearch),
      page: pageNo,
      limit,
      branch: clean(nextFilters.branch),
      storeId: clean(nextFilters.storeId),
      vehicleNumber: clean(nextFilters.vehicleNumber),
      vehicleCode: clean(nextFilters.vehicleCode),
      vehicleName: clean(nextFilters.vehicleName),
      vehicleSubtypeId: clean(nextFilters.vehicleSubtypeId),
    }),
    [loginUserId, branchId, limit]
  );

  const buildViewAllParams = useCallback(
    (nextFilters = filtersRef.current) => ({
      branchId,
      vehicleType: vehicleTypeRef.current,
      branch: clean(nextFilters.branch),
      storeId: clean(nextFilters.storeId),
      vehicleNumber: clean(nextFilters.vehicleNumber),
      vehicleCode: clean(nextFilters.vehicleCode),
      vehicleName: clean(nextFilters.vehicleName),
      vehicleSubtypeId: clean(nextFilters.vehicleSubtypeId),
    }),
    [branchId]
  );

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId,
          branchId,
          branchType: branch,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        setBranches([]);
        setStores([]);
        setVehicleSubtypes([]);
        return;
      }

      const dd = data?.dropdowns || {};
      setBranches(Array.isArray(dd.branches) ? dd.branches : []);
      setStores(Array.isArray(dd.stores) ? dd.stores : []);
      setVehicleSubtypes(
        Array.isArray(dd.vehicleSubtypes) ? dd.vehicleSubtypes : []
      );

      setFilters((prev) => ({
        ...prev,
        branch: toText(branchId),
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Init failed");
      setBranches([]);
      setStores([]);
      setVehicleSubtypes([]);
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, branch, commonHeaders]);

  const loadList = useCallback(
    async (pageNo = 1, nextFilters = filtersRef.current, nextSearch = searchRef.current) => {
      const ok = validateBeforeAction({
        requireBranch: true,
        requireLogin: true,
      });
      if (!ok) return;

      try {
        setLoading(true);
        setHasSearched(true);
        resetSelection();

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(pageNo, nextFilters, nextSearch),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setList([]);
          setTotal(0);
          toast.error(data?.message || "List load failed");
          return;
        }

        const rows = Array.isArray(data.list) ? data.list : [];
        setList(rows);
        setTotal(toInt(data.total, 0));
        setPage(toInt(data.page, pageNo));

        if (rows.length === 0) toast.info("No Records found");
      } catch (err) {
        console.error(err);
        setList([]);
        setTotal(0);
        toast.error(err?.response?.data?.message || "List load failed");
      } finally {
        setLoading(false);
      }
    },
    [validateBeforeAction, resetSelection, buildListParams, commonHeaders]
  );

  const handleViewAll = useCallback(async () => {
    const ok = validateBeforeAction({ requireBranch: true });
    if (!ok) return;

    try {
      setLoading(true);
      setHasSearched(true);
      resetSelection();

      const { data } = await axios.get(`${API_BASE}/viewall`, {
        params: buildViewAllParams(filtersRef.current),
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "View all failed");
        return;
      }

      const rows = Array.isArray(data.list) ? data.list : [];
      setList(rows);
      setTotal(rows.length);
      setPage(1);

      if (rows.length === 0) toast.info("No Records found");
      else toast.success(`Loaded ${rows.length} records`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "View all failed");
    } finally {
      setLoading(false);
    }
  }, [validateBeforeAction, resetSelection, buildViewAllParams, commonHeaders]);

  const performDelete = useCallback(async () => {
    if (!selectedIds.length) {
      toast.warn("Please select at least one record to delete.");
      return;
    }

    const ok = validateBeforeAction({ requireLogin: true });
    if (!ok) return;

    try {
      setDeleting(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId,
        },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        toast.error(data?.message || "Delete failed");
        return;
      }

      toast.success(data?.message || "Deleted successfully!");
      setConfirmDlg((prev) => ({ ...prev, open: false }));
      await loadList(1, filtersRef.current, searchRef.current);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }, [selectedIds, loginUserId, validateBeforeAction, commonHeaders, loadList]);

  const handleDelete = useCallback(() => {
    if (!selectedIds.length) {
      toast.warn("Please select at least one record to delete.");
      return;
    }

    const ok = validateBeforeAction({ requireLogin: true });
    if (!ok) return;

    setConfirmDlg({
      open: true,
      title: "Delete Fuel Consumption",
      message: `Are you sure you want to delete ${selectedIds.length} record(s)? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      danger: true,
    });
  }, [selectedIds, validateBeforeAction]);

  const closeConfirm = useCallback(() => {
    if (deleting) return;
    setConfirmDlg((prev) => ({ ...prev, open: false }));
  }, [deleting]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    const resetFilters = {
      vehicleNumber: "",
      vehicleCode: "",
      storeId: "",
      branch: toText(branchId),
      vehicleSubtypeId: "",
      vehicleName: "",
    };

    setSearch("");
    searchRef.current = "";
    setFilters(resetFilters);
    filtersRef.current = resetFilters;
    setPage(1);
    resetSelection();
    setTimeout(() => loadList(1, resetFilters, ""), 0);
  }, [branchId, resetSelection, loadList]);

  const handleSearchClick = useCallback(async () => {
    setPage(1);
    await loadList(1, filtersRef.current, searchRef.current);
  }, [loadList]);

  const handlePage = useCallback(
    async (newPage) => {
      const safePage = Math.min(Math.max(1, newPage), totalPages);
      setPage(safePage);
      await loadList(safePage, filtersRef.current, searchRef.current);
    },
    [totalPages, loadList]
  );

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(
      list.map((x) => x.id).filter((x) => x !== null && x !== undefined)
    );
  }, [allSelected, list]);

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleRowView = useCallback(
    (row) => {
      localStorage.setItem("FuelConsumptionVehicleId", String(row.id));
      localStorage.setItem(
        "FuelConsumptionType",
        vehicleType === "OwnVehicle" ? "Own" : "Hire"
      );

      navigate("/vehicle/FuelConsumption", {
        state: {
          vehicleId: row.id,
          type: vehicleType,
        },
      });
    },
    [navigate, vehicleType]
  );

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    setPage(1);
    loadList(1, filtersRef.current, searchRef.current);
    // Reload only when vehicle type changes or on first mount.
    // Do not depend on loadList — that used to refetch on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">FUEL CONSUMPTION</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Fuel Consumption Listing
              </h2>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {isBusy && (
                <span
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-500"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : deleting
                    ? "Deleting..."
                    : loading
                    ? "Searching..."
                    : "Please wait..."}
                </span>
              )}

              <button
                type="button"
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    type="button"
                    onClick={handleSearchClick}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="md:col-span-2 lg:col-span-3">
                  <label className={labelClass}>Vehicle Type</label>
                  <div className="flex items-center gap-6 h-[42px]">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="HireVehicle"
                        checked={vehicleType === "HireVehicle"}
                        onChange={(e) => setVehicleType(e.target.value)}
                        disabled={isBusy}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        Hire Vehicle
                      </span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="OwnVehicle"
                        checked={vehicleType === "OwnVehicle"}
                        onChange={(e) => setVehicleType(e.target.value)}
                        disabled={isBusy}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        Own Vehicle
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    name="vehicleNumber"
                    value={filters.vehicleNumber}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle code"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearchClick();
                    }}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter vehicle name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearchClick();
                    }}
                  />
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={pageLoading}
                  >
                    <option value="">Select Store</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={pageLoading}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Subtype</label>
                  <select
                    name="vehicleSubtypeId"
                    value={filters.vehicleSubtypeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={pageLoading}
                  >
                    <option value="">Select Vehicle Subtype</option>
                    {vehicleSubtypes.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <label className={labelClass}>Search</label>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={inputClass}
                    placeholder="Search by code / name / number..."
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearchClick();
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading && list.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {pageLoading ? "Loading..." : "Searching..."}
                </div>
              </div>
            ) : list.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {hasSearched
                    ? "No Records found"
                    : "Please search to view records"}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        S.No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Number
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Supplier
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        View
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                          disabled={isBusy}
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    {list.map((item, idx) => {
                      const checked = selectedIds.includes(item.id);

                      return (
                        <tr
                          key={`${item.id}-${idx}`}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {(page - 1) * limit + idx + 1}
                          </td>

                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            {item.code || "-"}
                          </td>

                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {item.number || "-"}
                          </td>

                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {item.name || "-"}
                          </td>

                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {item.type || "-"}
                          </td>

                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {item.supplier || "-"}
                          </td>

                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleRowView(item)}
                              className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                              title="View"
                              disabled={isBusy}
                            >
                              ✏️
                            </button>
                          </td>

                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(item.id)}
                              className="w-4 h-4 cursor-pointer"
                              disabled={isBusy}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isBusy || selectedIds.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      selectedIds.length === 0 || isBusy
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    } bg-red-600 hover:bg-red-700 text-white`}
                  >
                    <FaTrash />
                    {deleting ? "Deleting..." : "Remove"}
                  </button>

                  <div className="flex items-center gap-3 flex-wrap">
                    <span
                      className={`text-sm ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Page <b>{page}</b> / <b>{totalPages}</b>
                      <span className="ml-3">
                        Total: <b>{total}</b>
                      </span>
                    </span>

                    <button
                      type="button"
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                        isDark
                          ? "border-gray-600 text-white hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      } ${
                        isBusy || page <= 1
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={isBusy || page <= 1}
                      onClick={() => handlePage(page - 1)}
                    >
                      Prev
                    </button>

                    <button
                      type="button"
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                        isDark
                          ? "border-gray-600 text-white hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      } ${
                        isBusy || page >= totalPages
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={isBusy || page >= totalPages}
                      onClick={() => handlePage(page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <ConfirmDialog
            open={confirmDlg.open}
            isDark={isDark}
            title={confirmDlg.title}
            message={confirmDlg.message}
            confirmText={confirmDlg.confirmText}
            cancelText={confirmDlg.cancelText}
            danger={confirmDlg.danger}
            primaryBtnClass={primaryBtnClass}
            ghostBtnClass={ghostBtnClass}
            busy={deleting}
            onCancel={closeConfirm}
            onConfirm={performDelete}
          />

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default FuelConsumptionListing;